import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Plus, Trash, Settings, Eye, EyeOff, Minus } from "lucide-react";
import {
  Autocomplete,
  FormControl,
  InputBase,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { VscInsert } from "react-icons/vsc";
import useApi from "../../hooks/useApi";
import useColumnManager from "../../hooks/useColumnManager";
import InvoiceSuggestionList from "../salesInvoice/InvoiceSuggestionList";
import ColumnManagingModal from "../salesInvoice/ColumnManagingModal";
import ProductListing from "../salesInvoice/ProductsModal";
import Modal from "../common/CommonModal";
import ReuseSidebar from "../salesInvoice/ReuseSidebar";
import AllDoSidebar from "../salesInvoice/AllDoSidebar";
import GenericComponent from "../salesInvoice/GenericComponent";
import canUserSeeThis from "../common/CheckCanUserSeeThis";
import checkIfUserHasAccess from "../common/UserCheckAccess";
import { useNavigate } from "react-router-dom";

const GenericInvoiceTable = ({
  items,
  onItemsChange,
  setShowSidebar,
  // setItemInfo,
  setCurrentLocation,
  extraFields = [],
  documentData,
  config,
  currentLocation,
  initialRowUnitOptions,
  onShowOtherCost,
  showOtherCostButton = false,
  isOtherCostVisible = false,
  otherCosts = [],
  setOtherCosts,
  salesmanOptions,
  setFocusedProductCode,
  readOnly = false,
  hideAddButton = true,
  currentStockInhand,
  mode,
  documentType,
  itemPricing,
  disabledFields = [],
}) => {
  const COMPANY_CODE = localStorage.getItem("companyCode");
  const DEPARTMENT =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  let API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0].API_CONFIG || "https://cubixweberp.com:213";
  const departmentLocation = localStorage.getItem("departmentLocation") || "A";
  const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser"))
    : "";
  const VATPERCENTAGE = CURRENT_USER_OBJECT[0]?.vatp;
  const masterCompanyCode = localStorage.getItem("masterCompanyCode") || "-";

  // Destructure config with defaults
  const {
    columnFieldMap,
    defaultColumnsOrder,
    getBaseColumn,
    calculateRowValues,
    screenCode,
    defaultColumnPercents,
    requiredFields = [
      "code",
      "description",
      "location",
      "unit",
      "qty",
      "unitPrice",
    ],
    newItemTemplate,
    getVatPercent,
  } = config;

  const hoverTimeoutRef = useRef(null);
  const isInteractingRef = useRef(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [activeSuggestionRowId, setActiveSuggestionRowId] = useState(null);

  // Debouncing and caching
  const debounceTimeoutRef = useRef(null);
  const suggestionCacheRef = useRef(new Map());
  const currentRequestRef = useRef(null);
  const isShowingStockAlertRef = useRef(false);
  const minusQtyApprovedRowsRef = useRef(new Set());
  const isShowingBelowCostAlertRef = useRef(false);

  const [locationOptions, setLocationOptions] = useState([]);
  const [rowUnitOptions, setRowUnitOptions] = useState({});
  const [unitsFetchedForRows, setUnitsFetchedForRows] = useState(new Set());

  const [focusedRowId, setFocusedRowId] = useState(null);
  const [focusedCell, setFocusedCell] = useState({ rowId: null, field: null });
  const [editingDescriptionCell, setEditingDescriptionCell] = useState(null);
  const [descriptionOriginalValue, setDescriptionOriginalValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stockRecheckTrigger, setStockRecheckTrigger] = useState(0);
  const [activeComponent, setActiveComponent] = useState(null);
  const [selectedProductForSidebar, setSelectedProductForSidebar] =
    useState(null);
  const [modalSuggestions, setModalSuggestions] = useState([]);
  const [searchBy, setSearchBy] = useState("UNIVERSAL");

  const [suggestionPage, setSuggestionPage] = useState(1);
  const [hasMoreSuggestions, setHasMoreSuggestions] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");

  const [modalPageNumber, setModalPageNumber] = useState(1);
  const [modalPageSize] = useState(250);
  const [modalTotalRows, setModalTotalRows] = useState(0);
  const [modalSearchQuery, setModalSearchQuery] = useState("-");

  const PAGE_SIZE = 50;

  const { request, loading } = useApi();
  const { request: modalRequest, loading: modalLoading } = useApi();
  const navigate = useNavigate();

  const modeMap = {
    AllTransactions: "All_Transactions",
    DeliveryOrder: "Dorder",
    SalesOrder: "Sorder",
    Quotations: "Quot",
    PendingOrder: "Porder",
    OrderRequest: "Orderreq",
    SalesmanRequest: "Smanreq",
    Claim: "Claim",
    Sales: "Sales",
    Purchase: "Purchase",
    QuotationTrack: "Quot_Track",
    Transit: "Transit",
    ItemDetails: "ItemDetails",
  };

  const renderSidebarContent = () => {
    if (!activeComponent || !selectedProductForSidebar) return null;

    const mode = modeMap[activeComponent.component];
    const componentProps = { selectedProduct: selectedProductForSidebar, mode };

    switch (activeComponent.component) {
      case "DeliveryOrder":
      case "PendingOrder":
      case "SalesOrder":
      case "Sales":
      case "SalesmanRequest":
      case "Purchase":
      case "AllTransactions":
      case "Transit":
      case "QuotationTrack":
      case "Quotations":
      case "OrderRequest":
      case "Claim":
        return (
          <AllDoSidebar
            {...componentProps}
            exportFileName={activeComponent?.fullLabel}
          />
        );
      default:
        return (
          <GenericComponent title={activeComponent.label} {...componentProps} />
        );
    }
  };

  // Validation helper function
  const validateRequiredFields = useCallback(() => {
    const fieldDisplayNames = Object.entries(columnFieldMap).reduce(
      (acc, [label, field]) => ({ ...acc, [field]: label }),
      {},
    );

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      for (let field of requiredFields) {
        const value = item[field];
        const isEmpty =
          !value ||
          (typeof value === "string" && value.trim() === "") ||
          (field === "qty" && (value === 0 || value === "0")) ||
          (field === "unitPrice" && (value === 0 || value === "0"));

        if (isEmpty) {
          alert(
            `Please fill in the ${
              fieldDisplayNames[field] || field
            } field for row ${i + 1} before adding a new row.`,
          );

          setTimeout(() => {
            const fieldInput = document.querySelector(
              `[data-row-id="${item.id}"][data-field="${field}"] input, [data-row-id="${item.id}"][data-field="${field}"] .MuiSelect-select`,
            );
            if (fieldInput) {
              fieldInput.focus();
            }
          }, 100);

          return false;
        }
      }
    }
    return true;
  }, [items, requiredFields, columnFieldMap]);

  // Calculate mapped fields and default column percentages
  const allFields = useMemo(
    () => [...defaultColumnsOrder, ...extraFields],
    [extraFields, defaultColumnsOrder],
  );

  const mappedFields = useMemo(
    () => allFields.map((label) => columnFieldMap[label] || label),
    [allFields, columnFieldMap],
  );

  // Function to generate columns from data
  const generateColumnsFunction = useCallback(
    (data) => {
      return mappedFields.map((field) => ({ field }));
    },
    [mappedFields],
  );

  const {
    tableRef,
    columnVisibilityModel,
    columnsOrder,
    showColumnManager,
    setShowColumnManager,
    draggedColumn,
    dragOverIndex,
    isDragging,
    finalColumns: columnManagerColumns,
    handleColumnResize,
    handleVisibilityChange,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    moveColumn,
    resetColumnOrder,
    toggleColumnVisibility,
    getColumnDisplayName,
  } = useColumnManager({
    data: items,
    screenCode,
    baseUrl: BASE_URL,
    companyCode: COMPANY_CODE,
    defaultColumnPercents,
    defaultColumnsOrder: mappedFields,
    generateColumnsFunction,
    request,
  });

  const validateCodeField = useCallback(() => {
    const emptyCodeItems = items.filter(
      (item) => !item.code || item.code.trim() === "",
    );

    if (emptyCodeItems.length > 0) {
      alert(
        "Please fill in the code field for all existing items before adding a new row.",
      );

      const firstEmptyItem = emptyCodeItems[0];
      setTimeout(() => {
        const codeInput = document.querySelector(
          `[data-row-id="${firstEmptyItem.id}"][data-field="code"] input`,
        );
        if (codeInput) {
          codeInput.focus();
        }
      }, 100);

      return false;
    }
    return true;
  }, [items]);

  useEffect(() => {
    if (
      initialRowUnitOptions &&
      Object.keys(initialRowUnitOptions).length > 0
    ) {
      setRowUnitOptions(initialRowUnitOptions);
    }
  }, [initialRowUnitOptions]);

  // Fetch total count for modal stock listing
  const fetchModalTotalCount = useCallback(
    async (query = "-", searchByValue = searchBy) => {
      try {
        const effectiveQuery =
          query?.trim() === "" ? "-" : query?.trim() || "-";
        const formattedQuery = effectiveQuery.replace(/\//g, "%2F");
        const url = `${BASE_URL}/api/cpayscount/${COMPANY_CODE}/ITEMCOUNT/${searchByValue}/${formattedQuery}`;
        const response = await modalRequest({
          url: url,
          method: "GET",
          // timeout: 5000,
        });
        if (Array.isArray(response) && response[0]?.CNT) {
          setModalTotalRows(Number(response[0].CNT));
        } else {
          setModalTotalRows(0);
        }
      } catch (err) {
        console.error("Failed to fetch total count", err);
        setModalTotalRows(0);
      }
    },
    [BASE_URL, COMPANY_CODE, searchBy],
  );

  // For Product Modal (Paginated API)
  const fetchProductDataForModal = useCallback(
    async (
      query = "-",
      searchByValue = searchBy,
      location = departmentLocation,
      page = modalPageNumber,
      limit = modalPageSize,
    ) => {
      const effectiveQuery = query?.trim() === "" ? "-" : query?.trim() || "-";
      const formattedQuery = effectiveQuery.replace(/\//g, "%2F");

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const url = `${BASE_URL}/api/CRMStockListPage/${COMPANY_CODE}/${searchByValue}/${formattedQuery}/${location}/-/${page}/${limit}`;

        const response = await modalRequest({
          url: url,
          method: "GET",
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response) {
          setModalSuggestions(response);
          return response;
        } else {
          setModalSuggestions([]);
          return [];
        }
      } catch (err) {
        if (err.name === "AbortError") {
          console.log("Request was cancelled");
        } else {
          console.error("Failed to fetch product data", err);
        }
        setModalSuggestions([]);
        return [];
      }
    },
    [
      searchBy,
      BASE_URL,
      COMPANY_CODE,
      modalPageNumber,
      modalPageSize,
      departmentLocation,
    ],
  );

  // For Suggestions (with pagination)
  const fetchSuggestionsWithPagination = useCallback(
    async (query = "", page = 1, location = departmentLocation) => {
      const formattedQuery = query.replace(/\//g, "%2F");

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const url = `${BASE_URL}/api/CRMStocksubList/${COMPANY_CODE}/${searchBy}/${formattedQuery}/${location}/-/${page}/${PAGE_SIZE}`;

        const response = await request({
          url: url,
          method: "GET",
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        return response || [];
      } catch (err) {
        if (err.name === "AbortError") {
          console.log("Request was cancelled");
        } else {
          console.error("Failed to fetch suggestions", err);
        }
        return [];
      }
    },
    [searchBy, BASE_URL, COMPANY_CODE, PAGE_SIZE],
  );

  const debouncedFetchSuggestions = useCallback(
    async (query, rowId) => {
      setCurrentSearchQuery(query);

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (!query || query.length < 1) {
        setSuggestions([]);
        setShowSuggestions(false);
        setSuggestionPage(1);
        setHasMoreSuggestions(true);
        return;
      }

      const currentRow = items.find((r) => r.id === rowId);
      const rowLocation = currentRow?.location || departmentLocation;

      const cacheKey = `${searchBy}_${query.toLowerCase()}_${rowLocation}_page1`;

      if (suggestionCacheRef.current.has(cacheKey)) {
        const cachedData = suggestionCacheRef.current.get(cacheKey);
        setSuggestions(cachedData);
        setShowSuggestions(true);
        setActiveSuggestionRowId(rowId);
        setSelectedSuggestionIndex(cachedData.length > 0 ? 0 : -1);
        setSuggestionPage(1);
        setHasMoreSuggestions(cachedData.length === PAGE_SIZE);
        return;
      }

      debounceTimeoutRef.current = setTimeout(async () => {
        const data = await fetchSuggestionsWithPagination(
          query,
          1,
          rowLocation,
        );

        setSuggestions(data);
        setShowSuggestions(true);
        setActiveSuggestionRowId(rowId);
        setSelectedSuggestionIndex(data.length > 0 ? 0 : -1);
        setSuggestionPage(1);
        setHasMoreSuggestions(data.length === PAGE_SIZE);
      }, 300);
    },
    [
      searchBy,
      fetchSuggestionsWithPagination,
      PAGE_SIZE,
      items,
      departmentLocation,
    ],
  );

  const loadMoreSuggestions = useCallback(
    async (query) => {
      if (isFetchingMore || !hasMoreSuggestions) return;

      setIsFetchingMore(true);
      const nextPage = suggestionPage + 1;

      const currentRow = items.find((r) => r.id === activeSuggestionRowId);
      const rowLocation = currentRow?.location || departmentLocation;

      try {
        const data = await fetchSuggestionsWithPagination(
          query,
          nextPage,
          rowLocation,
        );

        if (data.length > 0) {
          setSuggestions((prev) => [...prev, ...data]);
          setSuggestionPage(nextPage);

          if (data.length < PAGE_SIZE) {
            setHasMoreSuggestions(false);
          }
        } else {
          setHasMoreSuggestions(false);
        }
      } finally {
        setIsFetchingMore(false);
      }
    },
    [
      suggestionPage,
      isFetchingMore,
      hasMoreSuggestions,
      fetchSuggestionsWithPagination,
      items,
      activeSuggestionRowId,
      departmentLocation,
    ],
  );

  const openProductModal = async (rowId, query = "-") => {
    setFocusedRowId(rowId);
    setModalSearchQuery(query);
    setModalPageNumber(1);
    fetchModalTotalCount(query, searchBy);
    setIsModalOpen(true);
    setShowSidebar(false);
  };

  const handleModalSearchQueryChange = useCallback(
    async (query = "-") => {
      setModalSearchQuery(query);
      setModalPageNumber(1);
      fetchModalTotalCount(query, searchBy);
    },
    [fetchModalTotalCount, searchBy],
  );

  useEffect(() => {
    if (isModalOpen) {
      const currentRow = items.find((r) => r.id === focusedRowId);
      const rowLocation = currentRow?.location || departmentLocation;
      fetchProductDataForModal(
        modalSearchQuery,
        searchBy,
        rowLocation,
        modalPageNumber,
        modalPageSize,
      );
    }
  }, [
    modalPageNumber,
    modalPageSize,
    isModalOpen,
    searchBy,
    fetchProductDataForModal,
    modalSearchQuery,
    departmentLocation,
    focusedRowId,
    items,
  ]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if we are interacting within this table
      const isInThisTable =
        tableRef.current && tableRef.current.contains(e.target);

      // Check if we're in a modal
      const isInModal =
        e.target.closest(".modal-container") ||
        document.querySelector('[role="dialog"]') ||
        document.querySelector(".MuiModal-root");

      const isInAccountField =
        e.target.hasAttribute("data-field-type") &&
        e.target.getAttribute("data-field-type") === "account";

      if (e.key === "F2" && focusedRowId !== null && !isModalOpen) {
        if (isInThisTable && !isInModal && !isInAccountField) {
          e.preventDefault();
          e.stopPropagation();
          openProductModal(focusedRowId);
        }
      }

      // Handle function keys for sidebar components
      if (
        /^F\d+$/.test(e.key) &&
        e.key !== "F2" &&
        focusedRowId !== null &&
        !isModalOpen
      ) {
        if (isInThisTable && !isInModal && !isInAccountField) {
          const focusedItem = items?.find((i) => i.id === focusedRowId);
          if (focusedItem && focusedItem.code) {
            e.preventDefault();
            e.stopPropagation();

            if (e.key === "F3") {
              if (canUserSeeThis("AE")) {
                navigate("/itemCreation", { state: focusedItem.code });
                return;
              }
            }

            const functionKeys = {
              F1: {
                component: "SalesOrder",
                label: "Sales Order Pending",
                fullLabel: "Sales Order Pending",
                button: "F1",
              },
              F4: {
                component: "OrderRequest",
                label: "Order request",
                fullLabel: "Order Request",
                button: "F4",
              },
              F7: {
                component: "Sales",
                label: "Sales History",
                fullLabel: "Sales History",
                button: "F7",
              },
              F8: {
                component: "Purchase",
                label: "Purchase History",
                fullLabel: "Purchase History",
                button: "F8",
              },
              F9: {
                component: "AllTransactions",
                label: "Complete Transaction History",
                fullLabel: "Complete Transaction History",
                button: "F9",
              },
              F10: {
                component: "PendingOrder",
                label: "Pending Purchase Order",
                fullLabel: "Pending Purchase Order",
                button: "F10",
              },
              F11: {
                component: "QuotationHistory",
                label: "Quotation History",
                fullLabel: "Quotation History",
                button: "F11",
              },
              F12: {
                component: "OrderRequestHistory",
                label: "Order Request History",
                fullLabel: "Order Request History",
                button: "F12",
              },
            };

            let accessKeyAndFunctionKeyMap = {
              F1: "",
              F2: "JT",
              F4: "JS",
              F7: "JO",
              F8: "JN",
              F9: "JV",
              F11: "JU",
            };

            let requiredKey = accessKeyAndFunctionKeyMap[e.key];
            if (requiredKey === undefined) {
              return; // unsupported or removed F key
            }

            let result =
              requiredKey === "" ? true : canUserSeeThis(requiredKey);
            if (result) {
              const buttonConfig = functionKeys[e.key];
              if (buttonConfig) {
                setActiveComponent(buttonConfig);
                setSelectedProductForSidebar({
                  Code: focusedItem.code,
                  id: focusedItem.code,
                });
                setSidebarOpen(true);
              }
            }
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [focusedRowId, isModalOpen, items, navigate]);

  // pass context to calculateRowValues so the purchase config can distribute other costs
  const calculatedItems = useMemo(() => {
    // supply items and otherCosts to the calculator
    return items?.map((item) =>
      calculateRowValues(item, documentData, { items, otherCosts }),
    );
  }, [items, documentData, calculateRowValues, otherCosts]);

  // MODIFIED: Fetch unit options only when dropdown is opened
  const fetchUnitOptions = useCallback(
    async (productCode, rowId, currentUnit) => {
      // Check if already fetched
      if (unitsFetchedForRows.has(rowId)) {
        return rowUnitOptions[rowId] || [];
      }

      const safeProductCode = encodeURIComponent(productCode);
      const url = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/UNITS/${safeProductCode}/-`;

      try {
        const res = await fetch(url);
        const data = await res.json();
        const units = data.map((u) => ({ value: u.Unit, label: u.Unit }));

        // Ensure current unit is in list
        if (currentUnit && !units.some((u) => u.value === currentUnit)) {
          units.unshift({ value: currentUnit, label: currentUnit });
        }

        // Store fetched units
        setRowUnitOptions((prev) => ({ ...prev, [rowId]: units }));
        setUnitsFetchedForRows((prev) => new Set([...prev, rowId]));

        return units;
      } catch (err) {
        return [];
      }
    },
    [
      BASE_URL,
      COMPANY_CODE,
      rowUnitOptions,
      unitsFetchedForRows,
      setRowUnitOptions,
      setUnitsFetchedForRows,
    ],
  );

  useEffect(() => {
    const mapLocationApiResponse = (apiResponse) =>
      apiResponse.map((loc) => ({
        value: loc.loc_code.trim(),
        label: loc.DESC.trim(),
        locn: loc.locn ? loc.locn.trim() : "",
      }));

    const fetchLocationsAndSetDefault = async () => {
      try {
        // First, fetch all locations
        const locationRes = await fetch(
          `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/LOCATONLIST_ALL/${DEPARTMENT}/-`,
        );
        const locationData = await locationRes.json();
        const mappedLocations = mapLocationApiResponse(locationData);
        // console.log("Mapped Locations:", mappedLocations);
        setLocationOptions(mappedLocations);
      } catch (err) {
        console.error("Error fetching locations or departments:", err);
        setLocationOptions([]);
      }
    };

    fetchLocationsAndSetDefault();
  }, [DEPARTMENT, BASE_URL, COMPANY_CODE]);

  const insertRowAbove = () => {
    if (!validateCodeField()) {
      return;
    }

    if (selectedRowIds.length === 1) {
      const selectedId = selectedRowIds[0];
      const index = items.findIndex((item) => item.id === selectedId);

      const newRowId = Date.now();
      const currentItemLocation = items[index]?.location || departmentLocation;
      const newItem = newItemTemplate(newRowId, index + 1, currentItemLocation);

      const newItems = [...items];
      newItems.splice(index, 0, newItem);

      const reindexedItems = newItems.map((item, idx) => ({
        ...item,
        sino: idx + 1,
      }));

      onItemsChange(reindexedItems);

      setTimeout(() => {
        setFocusedCell({ rowId: newRowId, field: "code" });
        setFocusedRowId(newRowId);

        const newInput = document.querySelector(
          `[data-row-id="${newRowId}"][data-field="code"] input`,
        );
        if (newInput) {
          newInput.focus();
        }
      }, 100);
    }
  };

  const addItem = useCallback(() => {
    if (!validateCodeField()) {
      return;
    }

    const newRowId = Date.now();
    const lastItemLocation =
      items.length > 0 ? items[items.length - 1].location : departmentLocation;
    const newItem = newItemTemplate(
      newRowId,
      items.length + 1,
      lastItemLocation || departmentLocation,
    );

    onItemsChange([...items, newItem]);

    setTimeout(() => {
      setFocusedCell({ rowId: newRowId, field: "code" });
      setFocusedRowId(newRowId);

      const newInput = document.querySelector(
        `[data-row-id="${newRowId}"][data-field="code"] input`,
      );
      if (newInput) {
        newInput.focus();
      }
    }, 100);
  }, [
    items,
    onItemsChange,
    departmentLocation,
    validateCodeField,
    newItemTemplate,
  ]);

  const updateItem = useCallback(
    (id, field, value) => {
      const priceField = Object.values(config.columnFieldMap).includes(
        "unitCost",
      )
        ? "unitCost"
        : "unitPrice";

      const updatedItems = items.map((item) => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };

          // If unit becomes FOC, force price to 0
          if (field === "unit" && value === "FOC") {
            updatedItem[priceField] = 0;
            delete updatedItem._manualTotalEdit;
          }

          // When VAT toggle changes, force recalculation by clearing manual flag
          if (field === "vatEnabled") {
            delete updatedItem._manualTotalEdit;
          }

          // Clear qty when reactivating an inactive item
          if (
            field === "isActive" &&
            value === "Yes" &&
            item.isActive === "No"
          ) {
            updatedItem.qty = "";
          }

          return updatedItem;
        }
        return item;
      });

      onItemsChange(updatedItems);

      // If the edited field is the location, notify parent so FloatingItemInformation refetches
      if (field === "location") {
        pendingStockCheckRef.current = id;
        const updatedRow = updatedItems.find((r) => r.id === id);
        const newLoc = (updatedRow?.location || "").toString().trim();
        if (newLoc) {
          setCurrentLocation(newLoc);
        }
      }
    },
    [items, onItemsChange, setCurrentLocation],
  );

  const deleteSelectedItems = useCallback(() => {
    if (selectedRowIds.length === 0) return;

    const updatedItems = items.filter(
      (item) => !selectedRowIds.includes(item.id),
    );

    const reindexedItems = updatedItems.map((item, index) => ({
      ...item,
      sino: index + 1,
    }));

    onItemsChange(reindexedItems);
    setSelectedRowIds([]);
  }, [items, selectedRowIds, onItemsChange]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === "a") {
        e.preventDefault();
        addItem();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [addItem]);

  const clearHoverTimeout = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const showSidebarWithItem = useCallback(
    (item) => {
      clearHoverTimeout();
      const targetLoc = (item?.location || currentLocation)?.trim();

      if (targetLoc !== currentLocation) {
        setCurrentLocation(targetLoc);
      }
      setShowSidebar(true);
    },
    [setCurrentLocation, setShowSidebar, clearHoverTimeout, currentLocation],
  );

  const hideSidebarWithDelay = useCallback(() => {
    if (!isInteractingRef.current) {
      hoverTimeoutRef.current = setTimeout(() => {
        setShowSidebar(false);
        if (setFocusedProductCode) {
          setFocusedProductCode(null);
        }
      }, 200);
    }
  }, [setShowSidebar, setFocusedProductCode]);

  const handleCellFocus = useCallback(
    (item) => {
      isInteractingRef.current = true;
      showSidebarWithItem(item);
      console.log(
        "[GenericInvoiceTable] handleCellFocus. Item code is:",
        item.code,
      );
      if (setFocusedProductCode) {
        setFocusedProductCode(item.code ? item.code : null);
      }
    },
    [showSidebarWithItem, setFocusedProductCode],
  );

  const handleCellBlur = useCallback(() => {
    isInteractingRef.current = false;
    hideSidebarWithDelay();
  }, [hideSidebarWithDelay]);

  const selectCodeSuggestion = useCallback(
    async (rowId, selectedItem) => {
      // console.log("selectedItem", selectedItem)
      const existingItem = items.find(
        (item) =>
          item.id !== rowId &&
          item.code &&
          item.code.toLowerCase() === selectedItem.Code.toLowerCase(),
      );

      if (existingItem) {
        const confirmed = window.confirm(
          `Item "${selectedItem.Code}" already exists.\n\nDo you want to continue and add this duplicate item?`,
        );

        if (!confirmed) {
          setShowSuggestions(false);
          setSelectedSuggestionIndex(-1);
          setActiveSuggestionRowId(null);
          return;
        }
      }

      // MODIFIED: Don't fetch units immediately, just set the default unit
      const defaultUnit = selectedItem.Unit || "";

      // PURCHASEINVOICE
      //PURCHASE_ORDER
      //SAMPLE_ISSUE
      //STOCKTRANSFERIN

      // Use Cost field for these screens, else SelPrice
      const screensUseCost = [
        "PURCHASEINVOICE",
        "PURCHASE_ORDER",
        "SAMPLE_ISSUE",
        "STOCKTRANSFERIN",
        "PRODUCTION",
      ];
      const screenCode = config.screenCode
        ? config.screenCode.toUpperCase()
        : "";
      const useCost = screensUseCost.some((code) => screenCode.includes(code));
      const priceField = Object.values(config.columnFieldMap).includes(
        "unitCost",
      )
        ? "unitCost"
        : "unitPrice";

      const oldItem = items.find((item) => item.id === rowId);
      const codeHasChanged = oldItem?.code !== selectedItem.Code;

      const updatedItems = items.map((item) => {
        if (item.id === rowId) {
          const assignedUnit = codeHasChanged ? defaultUnit : item.unit;
          const finalPrice =
            assignedUnit === "FOC"
              ? 0
              : useCost
                ? selectedItem.Cost ||
                  selectedItem.unitCost ||
                  selectedItem.UnitCost ||
                  selectedItem.UNISTCOST ||
                  0
                : selectedItem.SelPrice || selectedItem.SELPRICE || 0;

          return {
            ...item,
            code: (selectedItem.Code || "").trim(),
            description: selectedItem.Description,
            unit: assignedUnit,
            itemCost: selectedItem.Cost || 0,
            dac:
              selectedItem.dac ??
              selectedItem.Dac ??
              item.dac ??
              item.DAC ??
              "0",
            [priceField]: finalPrice,
          };
        }
        return item;
      });

      onItemsChange(updatedItems);

      if (codeHasChanged) {
        // Clear the cached options
        setRowUnitOptions((prev) => {
          const newState = { ...prev };
          delete newState[rowId];
          return newState;
        });

        // Mark units as "not fetched"
        setUnitsFetchedForRows((prev) => {
          const newSet = new Set(prev);
          newSet.delete(rowId);
          return newSet;
        });
      }

      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
      setActiveSuggestionRowId(null);

      setFocusedCell({ rowId, field: "qty" });
      setFocusedRowId(rowId);

      setTimeout(() => {
        const qtyInput = document.querySelector(
          `[data-row-id="${rowId}"][data-field="qty"] input`,
        );
        if (qtyInput) {
          qtyInput.focus();
        }
      }, 100);
    },
    [items, onItemsChange, config.columnFieldMap, setCurrentLocation],
  );

  const getVisibleFields = useCallback(() => {
    return columnManagerColumns.map((col) => col.field);
  }, [columnManagerColumns]);

  const navigateToNextCell = useCallback(
    (currentRowId, currentField) => {
      const visibleFields = getVisibleFields();
      const currentFieldIndex = visibleFields.indexOf(currentField);
      const currentRowIndex = items.findIndex(
        (item) => item.id === currentRowId,
      );

      // If user pressed Enter on amount/total => handle differently
      if (currentField === "amount" || currentField === "total") {
        // If Add Row feature is disabled, do NOT auto-create a new row.
        // Instead move focus to next row's code (if any), or no-op if last row.
        if (!hideAddButton) {
          if (currentRowIndex < items.length - 1) {
            const nextRowId = items[currentRowIndex + 1].id;
            setFocusedCell({ rowId: nextRowId, field: "code" });
            setFocusedRowId(nextRowId);
            setTimeout(() => {
              const nextInput = document.querySelector(
                `[data-row-id="${nextRowId}"][data-field="code"] input`,
              );
              if (nextInput) nextInput.focus();
            }, 50);
          }
          return;
        }

        // If Add Row feature is enabled, fall back to existing behavior:
        if (!validateCodeField()) {
          return;
        }

        const newRowId = Date.now();
        const newItem = newItemTemplate(
          newRowId,
          items.length + 1,
          currentLocation,
        );

        onItemsChange([...items, newItem]);

        setTimeout(() => {
          setFocusedCell({ rowId: newRowId, field: "code" });
          setFocusedRowId(newRowId);

          const newInput = document.querySelector(
            `[data-row-id="${newRowId}"][data-field="code"] input`,
          );
          if (newInput) newInput.focus();
        }, 100);

        return;
      }

      // existing navigation logic for other fields:
      if (currentFieldIndex < visibleFields.length - 1) {
        const nextField = visibleFields[currentFieldIndex + 1];
        setFocusedCell({ rowId: currentRowId, field: nextField });

        setTimeout(() => {
          const nextInput = document.querySelector(
            `[data-row-id="${currentRowId}"][data-field="${nextField}"] input, [data-row-id="${currentRowId}"][data-field="${nextField}"] .MuiSelect-select`,
          );
          if (nextInput) {
            nextInput.focus();
          }
        }, 50);
      } else {
        if (currentRowIndex === items.length - 1) {
          if (!validateCodeField()) {
            return;
          }

          // Creating a new row here should obey hideAddButton too
          if (!hideAddButton) return;

          const newRowId = Date.now();
          const newItem = newItemTemplate(
            newRowId,
            items.length + 1,
            departmentLocation,
          );

          onItemsChange([...items, newItem]);

          setTimeout(() => {
            setFocusedCell({ rowId: newRowId, field: "code" });
            setFocusedRowId(newRowId);

            const newInput = document.querySelector(
              `[data-row-id="${newRowId}"][data-field="code"] input`,
            );
            if (newInput) {
              newInput.focus();
            }
          }, 100);
        } else {
          const nextRowId = items[currentRowIndex + 1].id;
          setFocusedCell({ rowId: nextRowId, field: "code" });

          setTimeout(() => {
            const nextInput = document.querySelector(
              `[data-row-id="${nextRowId}"][data-field="code"] input`,
            );
            if (nextInput) {
              nextInput.focus();
            }
          }, 50);
        }
      }
    },
    [
      getVisibleFields,
      items,
      onItemsChange,
      departmentLocation,
      validateCodeField,
      newItemTemplate,
      hideAddButton,
    ],
  );

  const navigateWithArrowKeys = useCallback(
    (currentRowId, currentField, direction) => {
      const visibleFields = getVisibleFields();
      const currentFieldIndex = visibleFields.indexOf(currentField);
      const currentRowIndex = items.findIndex(
        (item) => item.id === currentRowId,
      );

      let targetRowIndex = currentRowIndex;
      let targetField = currentField;

      switch (direction) {
        case "up":
          if (currentRowIndex > 0) {
            targetRowIndex = currentRowIndex - 1;
          }
          break;
        case "down":
          if (currentRowIndex < items.length - 1) {
            targetRowIndex = currentRowIndex + 1;
          }
          break;
        case "left":
          if (currentFieldIndex > 0) {
            targetField = visibleFields[currentFieldIndex - 1];
          }
          break;
        case "right":
          if (currentFieldIndex < visibleFields.length - 1) {
            targetField = visibleFields[currentFieldIndex + 1];
          }
          break;
        default:
      }

      const targetRowId = items[targetRowIndex].id;

      if (targetRowId !== currentRowId || targetField !== currentField) {
        setFocusedCell({ rowId: targetRowId, field: targetField });
        setFocusedRowId(targetRowId);

        setTimeout(() => {
          const targetInput = document.querySelector(
            `[data-row-id="${targetRowId}"][data-field="${targetField}"] input, [data-row-id="${targetRowId}"][data-field="${targetField}"] .MuiSelect-select`,
          );
          if (targetInput) {
            targetInput.focus();
          }
        }, 50);
      }
    },
    [getVisibleFields, items, setFocusedCell, setFocusedRowId],
  );

  const updateItemWithTotalRecalculation = useCallback(
    (id, field, value) => {
      const updatedItems = items.map((item) => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };

          if (field === "total") {
            // Mark that user is manually editing total
            // This flag will be preserved and used by calculateRowValues
            updatedItem._manualTotalEdit = true;
          }

          return updatedItem;
        }
        return item;
      });

      onItemsChange(updatedItems);
    },
    [items, onItemsChange],
  );

  const pendingStockCheckRef = useRef(null);

  const validateStock = useCallback(
    (item, requestedQty, availableStock, isBlur = false) => {
      // console.log("item", item)
      if (
        ![
          "SALESINVOICE",
          "SALESORDER",
          "PRODUCTION",
          "DELIVERYORDER",
          "LOCATIONTRANSFER",
          "SAMPLEISSUE",
        ].includes(documentType)
      )
        return true;

      // Skip validation for service items (dac === "1")
      if (
        item.dac === "1" ||
        item.dac === true ||
        item.DAC === true ||
        itemPricing?.dac === true
      )
        return true;

      // Skip validation for inactive items
      if (String(item.isActive || "").toLowerCase() === "no") return true;

      // DORD transfer + SUNRISEGROUP: only block if stock is negative, skip user access checks
      if (
        documentData?.jv_num === "DORD" &&
        masterCompanyCode?.toUpperCase() === "SUNRISEGROUP"
      ) {
        if (requestedQty > availableStock && availableStock < 0) {
          isShowingStockAlertRef.current = true;
          alert(`Qty exceeded available qty (Available: ${availableStock})`);
          isShowingStockAlertRef.current = false;
          return false;
        }
        return true;
      }

      const currentLocation = locationOptions.find(
        (loc) => loc.value === item.location,
      );

      //  Prevent duplicate alerts
      if (isShowingStockAlertRef.current) return false;

      if (requestedQty > availableStock) {
        let effectiveStock = availableStock;
        if (mode === "edit") {
          const originalQty = parseFloat(item.originalQty) || 0;
          effectiveStock = originalQty + availableStock;
        }
        if (requestedQty <= effectiveStock) return true;

        let isBlocked = true;

        if (currentLocation && currentLocation.locn === "MINUS") {
          isBlocked = false; // Bypass the user restriction for these locations
        } else if (documentType === "SALESINVOICE") {
          isBlocked = checkIfUserHasAccess("AM");
        } else if (documentType === "BRANCHTRANSFER") {
          isBlocked = checkIfUserHasAccess("AM");
        } else if (documentType === "SALESORDER") {
          isBlocked = checkIfUserHasAccess("AP");
        } else if (documentType === "DELIVERYORDER") {
          isBlocked = checkIfUserHasAccess("FZ");
        } else if (documentType === "PRODUCTION") {
          isBlocked = checkIfUserHasAccess("SX");
        } else if (documentType === "LOCATIONTRANSFER") {
          isBlocked = checkIfUserHasAccess("FH");
        }

        if (isBlocked) {
          isShowingStockAlertRef.current = true;
          alert(`Qty exceeded available qty (Available: ${effectiveStock})`);
          isShowingStockAlertRef.current = false;
          return false;
        } else if (isBlur) {
          isShowingStockAlertRef.current = true;
          const confirmed = window.confirm(
            `Entered qty exceeds available qty (${effectiveStock}).\nDo you want to enter with minus qty?`,
          );
          isShowingStockAlertRef.current = false;
          if (!confirmed) return false;
          // Mark this item as approved for minus qty so re-focus doesn't re-trigger
          if (item?.id) {
            minusQtyApprovedRowsRef.current.add(item.id);
          }
        }
      }

      return true;
    },
    [documentType, locationOptions, mode, itemPricing],
  );

  useEffect(() => {
    if (focusedRowId === null || currentStockInhand === null) return;
    if (
      ![
        "SALESINVOICE",
        "SALESORDER",
        "PRODUCTION",
        "DELIVERYORDER",
        "LOCATIONTRANSFER",
        "SAMPLEISSUE",
      ].includes(documentType)
    )
      return;

    // Prevent re-entry while an alert/confirm is already open
    if (isShowingStockAlertRef.current) return;

    const focusedItem = items.find((it) => it.id === focusedRowId);

    //  Skip if qty hasn't been entered yet
    if (!focusedItem || !focusedItem.code) return;

    // Skip stock validation for inactive items
    if (String(focusedItem.isActive || "").toLowerCase() === "no") return;

    // DORD transfer + SUNRISEGROUP: only block if stock is negative, skip user access checks
    if (
      documentData?.jv_num === "DORD" &&
      masterCompanyCode?.toUpperCase() === "SUNRISEGROUP"
    ) {
      const stock = parseFloat(currentStockInhand) || 0;
      const qty = parseFloat(focusedItem.qty) || 0;
      if (qty > stock && stock < 0) {
        isShowingStockAlertRef.current = true;
        alert(`Qty exceeded available qty (Available: ${stock})`);
        isShowingStockAlertRef.current = false;
        const newQty = stock > 0 ? stock : 0;
        onItemsChange(
          items.map((it) =>
            it.id === focusedRowId ? { ...it, qty: newQty } : it,
          ),
        );
      }
      return;
    }

    const qty = parseFloat(focusedItem.qty) || 0;
    if (qty <= 0) return; //  Don't validate if qty not entered

    // Skip re-validation if this row was already approved for minus qty
    if (minusQtyApprovedRowsRef.current.has(focusedRowId)) return;

    const stock = parseFloat(currentStockInhand) || 0;

    let effectiveStock = stock;
    if (mode === "edit") {
      const originalQty = parseFloat(focusedItem.originalQty) || 0;
      effectiveStock = originalQty + stock;
    }

    if (qty > effectiveStock) {
      const currentLocation = locationOptions.find(
        (loc) => loc.value === focusedItem.location,
      );

      let isBlocked = true;
      if (currentLocation && currentLocation.locn === "MINUS") {
        isBlocked = false;
      } else if (documentType === "SALESINVOICE") {
        isBlocked = checkIfUserHasAccess("AM");
      } else if (documentType === "SALESORDER") {
        isBlocked = checkIfUserHasAccess("AP");
      } else if (documentType === "DELIVERYORDER") {
        isBlocked = checkIfUserHasAccess("FZ");
      } else if (documentType === "PRODUCTION") {
        isBlocked = checkIfUserHasAccess("SX");
      } else if (documentType === "LOCATIONTRANSFER") {
        isBlocked = checkIfUserHasAccess("FH");
      }

      if (isBlocked) {
        // Hard-block: adjust qty to available stock (or 0), update directly without re-triggering validation
        isShowingStockAlertRef.current = true;
        alert(`Qty exceeded available qty (Available: ${effectiveStock})`);
        isShowingStockAlertRef.current = false;
        const newQty = effectiveStock > 0 ? effectiveStock : 0;
        onItemsChange(
          items.map((it) =>
            it.id === focusedRowId ? { ...it, qty: newQty } : it,
          ),
        );
      } else {
        // Non-blocked: show confirm dialog once
        isShowingStockAlertRef.current = true;
        const confirmed = window.confirm(
          `Entered qty exceeds available qty (${effectiveStock}).\nDo you want to enter with minus qty?`,
        );
        isShowingStockAlertRef.current = false;
        if (confirmed) {
          // User accepted minus qty — mark as approved so re-focus doesn't re-trigger
          minusQtyApprovedRowsRef.current.add(focusedRowId);
        }
        // Whether confirmed or not, don't forcibly update qty here;
        // the onBlur handler is responsible for final adjustment
      }
    }

    pendingStockCheckRef.current = null;
  }, [currentStockInhand, mode, stockRecheckTrigger]);

  const updateItemWithFlagReset = useCallback(
    (id, field, value) => {
      // Mark for stock re-validation if code or location changes
      if (field === "code" || field === "location") {
        pendingStockCheckRef.current = id;
        minusQtyApprovedRowsRef.current.delete(id);
        setStockRecheckTrigger((prev) => prev + 1);
      }

      // Find the current item *before* updating
      const currentItem = items.find((item) => item.id === id);
      // Check if the code is actually changing
      const codeIsChanging = field === "code" && currentItem?.code !== value;

      const priceField = Object.values(config.columnFieldMap).includes(
        "unitCost",
      )
        ? "unitCost"
        : "unitPrice";

      const updatedItems = items.map((item) => {
        if (item.id === id) {
          let newValue = value;

          // If unit is FOC and we're updating a price field, force it to 0
          if (
            (field === "unitPrice" || field === "unitCost") &&
            item.unit === "FOC"
          ) {
            newValue = 0;
          }

          const updatedItem = { ...item, [field]: newValue };

          // Clear manual edit flags when base calculation fields change
          if (
            field === "qty" ||
            field === "unitPrice" ||
            field === "unitCost" ||
            field === "Discount%" ||
            field === "DiscountAmount"
          ) {
            delete updatedItem._manualTotalEdit;
          }

          // Re-evaluate belowCost flag when price is manually edited
          if (field === "unitPrice" || field === "unitCost") {
            const enteredPrice = Number(newValue) || 0;
            const costAvgVal = Number(itemPricing?.costAvg) || 0;
            if (
              enteredPrice > 0 &&
              costAvgVal > 0 &&
              enteredPrice < costAvgVal
            ) {
              updatedItem._belowCost = true;
            } else {
              delete updatedItem._belowCost;
            }
          }

          // **NEW:** If the code is changing, clear the unit
          if (codeIsChanging) {
            updatedItem.unit = ""; // Clear the unit
          }

          return updatedItem;
        }
        return item;
      });
      onItemsChange(updatedItems);

      // **NEW:** If the code changed, invalidate the unit cache for this row
      if (codeIsChanging) {
        // Clear the cached options
        setRowUnitOptions((prev) => {
          const newState = { ...prev };
          delete newState[id]; // 'id' is the rowId
          return newState;
        });

        // Mark units as "not fetched"
        setUnitsFetchedForRows((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id); // 'id' is the rowId
          return newSet;
        });
      }

      // Ensure sidebar updates when code or location changes
      if (field === "code" || field === "location") {
        const updatedRow = updatedItems.find((r) => r.id === id);
        const newLoc = (updatedRow?.location || "").toString().trim();
        if (newLoc) {
          setCurrentLocation(newLoc);
        }
      }
    },
    [
      items,
      onItemsChange,
      focusedRowId,
      currentStockInhand,
      validateStock,
      setCurrentLocation,
      itemPricing,
    ],
  );

  const renderEditableCell = useCallback(
    (params, field, type = "text") => {
      const item = items.find((item) => item.id === params.row.id);
      const rowId = params.row.id;
      const isCodeField = field === "code";
      const isAmountField = field === "amount";
      const isTotalField = field === "total";
      const isCalculatedField = isAmountField || isTotalField;

      const isVatField = field === "vatPercent" || field === "vatAmount";
      const isVatDisabled = isVatField && item?.vatEnabled === false;

      const isPriceField = field === "unitPrice" || field === "unitCost";
      const isPriceDisabled = isPriceField && item?.unit === "FOC";

      const isDisabled = isVatDisabled || isPriceDisabled;

      let displayValue = params.value || "";
      if (isVatDisabled) displayValue = "0";
      if (isPriceDisabled) displayValue = "0";
      if (isTotalField && item._manualTotalEdit)
        displayValue = item.total || "";

      // --- DESCRIPTION TEXTAREA ---
      if (field === "description") {
        const isEditingDesc =
          editingDescriptionCell?.rowId === rowId &&
          editingDescriptionCell?.field === field;

        if (isEditingDesc) {
          return (
            <div
              style={{
                width: "100%",
                height: "100%",
                padding: "3px 4px",
                boxSizing: "border-box",
              }}
              data-row-id={rowId}
              data-field={field}
            >
              <textarea
                defaultValue={displayValue}
                autoFocus
                style={{
                  width: "100%",
                  minHeight: "60px",
                  height: "100%",
                  resize: "vertical",
                  padding: "4px 8px",
                  fontSize: "0.875rem",
                  fontFamily: "inherit",
                  border: "2px solid #3b82f6",
                  borderRadius: "3px",
                  outline: "none",
                  boxSizing: "border-box",
                  lineHeight: "1.4",
                }}
                onInput={(e) => {
                  e.target.style.height = "auto";
                  e.target.style.height =
                    Math.max(e.target.scrollHeight, 60) + "px";
                }}
                onFocus={(e) => {
                  e.stopPropagation();
                  setFocusedRowId(rowId);
                  setFocusedCell({ rowId, field });
                  const ta = e.target;
                  ta.style.height = "auto";
                  ta.style.height = Math.max(ta.scrollHeight, 60) + "px";
                }}
                onChange={(e) => {
                  e.stopPropagation();
                  updateItemWithFlagReset(rowId, field, e.target.value);
                }}
                onBlur={(e) => {
                  e.stopPropagation();
                  setEditingDescriptionCell(null);
                  handleCellBlur();
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Tab") {
                    e.preventDefault();
                    updateItemWithFlagReset(rowId, field, e.target.value);
                    setEditingDescriptionCell(null);
                    navigateToNextCell(rowId, field);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    updateItemWithFlagReset(
                      rowId,
                      field,
                      descriptionOriginalValue,
                    );
                    setEditingDescriptionCell(null);
                    handleCellBlur();
                  }
                }}
              />
            </div>
          );
        }

        // Normal display mode for description — click to enter textarea edit
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              padding: "3px 4px",
              boxSizing: "border-box",
            }}
            data-row-id={rowId}
            data-field={field}
          >
            <InputBase
              value={displayValue}
              readOnly
              inputProps={{
                title: String(displayValue),
                style: {
                  textAlign: "left",
                  padding: "0 8px",
                  height: "100%",
                  fontSize: "0.875rem",
                  fontFamily: "inherit",
                  cursor: "text",
                },
              }}
              sx={{
                width: "100%",
                height: "100%",
                border: "1px solid #e2e5e9",
                borderRadius: "3px",
                backgroundColor: "#fff",
                "& .MuiInputBase-input": {
                  height: "100%",
                  boxSizing: "border-box",
                  padding: "0 7px",
                  fontSize: "0.875rem",
                  fontFamily: "inherit",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
              onClick={() => {
                setEditingDescriptionCell({ rowId, field });
                setDescriptionOriginalValue(displayValue);
                setFocusedRowId(rowId);
                setFocusedCell({ rowId, field });
              }}
              onFocus={(e) => {
                e.stopPropagation();
                setEditingDescriptionCell({ rowId, field });
                setDescriptionOriginalValue(displayValue);
                handleCellFocus(item);
                setFocusedRowId(rowId);
                setFocusedCell({ rowId, field });
              }}
            />
          </div>
        );
      }
      // --- END DESCRIPTION TEXTAREA ---

      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            padding: "3px 4px",
            boxSizing: "border-box",
          }}
          data-row-id={rowId}
          data-field={field}
        >
          <InputBase
            type={type}
            value={displayValue}
            disabled={isDisabled}
            inputProps={{
              min: type === "number" ? 0 : undefined,
              title: String(displayValue),
              style: {
                textAlign: type === "number" ? "right" : "left",
                padding: "0 8px",
                height: "100%",
                fontSize: "0.875rem",
                fontFamily: "inherit",
                cursor: isDisabled ? "not-allowed" : "text",
              },
            }}
            sx={{
              width: "100%",
              height: "100%",
              border: "1px solid #e2e5e9",
              borderRadius: "3px",
              backgroundColor: isDisabled
                ? "#f3f4f6"
                : isCalculatedField
                  ? "#eff6ff"
                  : "#fff",
              "& .MuiInputBase-input": {
                height: "100%",
                boxSizing: "border-box",
                padding: "0 7px",
                fontSize: "0.875rem",
                fontFamily: "inherit",
                color: isDisabled ? "#9ca3af" : "inherit",
                cursor: isDisabled ? "not-allowed" : "text",
                textAlign: type === "number" ? "right" : "left",
                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                },
                /* hide number spinners — Firefox */
                "&[type=number]": {
                  MozAppearance: "textfield",
                },
              },
            }}
            onClick={() => {
              setFocusedRowId(rowId);
              setFocusedCell({ rowId, field });
            }}
            onFocus={(e) => {
              e.stopPropagation();
              handleCellFocus(item);
              setFocusedRowId(rowId);
              setFocusedCell({ rowId, field });
              if (isCodeField && params.value) {
                debouncedFetchSuggestions(params.value, rowId);
              }
            }}
            onChange={(e) => {
              e.stopPropagation();
              const value = e.target.value;
              if (isCalculatedField) {
                updateItemWithTotalRecalculation(rowId, field, value);
              } else {
                updateItemWithFlagReset(rowId, field, value);
              }
              if (isCodeField) {
                debouncedFetchSuggestions(value, rowId);
              }
            }}
            onBlur={(e) => {
              e.stopPropagation();

              // STOCK VALIDATION - Only for qty field
              if (field === "qty" && currentStockInhand !== null) {
                // Skip validation for inactive items
                if (String(item.isActive || "").toLowerCase() === "no") {
                  handleCellBlur();
                  return;
                }

                const qtyValue = parseFloat(e.target.value) || 0;
                const stockValue = parseFloat(currentStockInhand) || 0;

                // Block negative quantities
                if (qtyValue < 0) {
                  updateItemWithFlagReset(rowId, "qty", 0);
                  handleCellBlur();
                  return;
                }

                // Clear minus qty approval if qty changed (force re-validation)
                minusQtyApprovedRowsRef.current.delete(rowId);

                // Only validate if user actually entered a positive quantity
                if (qtyValue > 0 && qtyValue > stockValue) {
                  const isValid = validateStock(
                    item,
                    qtyValue,
                    stockValue,
                    true,
                  );
                  if (!isValid) {
                    // Only reset to available stock if positive, otherwise keep qty
                    if (stockValue > 0) {
                      updateItemWithFlagReset(rowId, "qty", stockValue);
                    }
                    // Don't proceed with other validations if stock validation failed
                    handleCellBlur();
                    setTimeout(() => {
                      if (activeSuggestionRowId === rowId) {
                        setShowSuggestions(false);
                        setActiveSuggestionRowId(null);
                      }
                    }, 150);
                    return;
                  }
                }
              }

              // PRICE VALIDATION - Only for price fields and SALESINVOICE
              const priceField = Object.values(config.columnFieldMap).includes(
                "unitCost",
              )
                ? "unitCost"
                : "unitPrice";

              if (
                (field === priceField ||
                  field === "unitPrice" ||
                  field === "unitCost") &&
                (documentType === "SALESINVOICE" ||
                  documentType === "SALESORDER")
              ) {
                const enteredPrice = parseFloat(e.target.value);

                // Only validate if price is actually entered (not empty, not zero)
                if (enteredPrice && enteredPrice > 0) {
                  const blockPrice = Number(itemPricing?.blockPrice) || 0;
                  const costAvg = Number(itemPricing?.costAvg) || 0;

                  const constraints = [];

                  // User has NO DN access => enforce Block Price
                  if (checkIfUserHasAccess("DN")) {
                    constraints.push(blockPrice);
                  }

                  // User has NO MN access => enforce Cost Avg
                  if (checkIfUserHasAccess("MN")) {
                    constraints.push(costAvg);
                  }

                  const minPrice =
                    constraints.length > 0 ? Math.max(...constraints) : 0;

                  if (minPrice > 0 && enteredPrice < minPrice) {
                    if (!isShowingBelowCostAlertRef.current) {
                      // guard against re-entry
                      isShowingBelowCostAlertRef.current = true;
                      alert(`Below cost`);
                      isShowingBelowCostAlertRef.current = false;
                      updateItemWithFlagReset(rowId, field, e.target.value); // keep entered value
                    }
                    handleCellBlur();
                    return;
                  }
                }
              }

              handleCellBlur();
              setTimeout(() => {
                if (activeSuggestionRowId === rowId) {
                  setShowSuggestions(false);
                  setActiveSuggestionRowId(null);
                }
              }, 150);
            }}
            onKeyDown={(e) => {
              if (e.key === " " && type !== "number") {
                e.stopPropagation();
              }

              if (isCodeField && showSuggestions && suggestions.length > 0) {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedSuggestionIndex((prev) =>
                    prev < suggestions.length - 1 ? prev + 1 : 0,
                  );
                  return;
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedSuggestionIndex((prev) =>
                    prev > 0 ? prev - 1 : suggestions.length - 1,
                  );
                  return;
                } else if (e.key === "Enter" && selectedSuggestionIndex >= 0) {
                  e.preventDefault();
                  e.stopPropagation();
                  selectCodeSuggestion(
                    activeSuggestionRowId,
                    suggestions[selectedSuggestionIndex],
                  );
                  return;
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowSuggestions(false);
                  setActiveSuggestionRowId(null);
                  setSelectedSuggestionIndex(-1);
                  return;
                }
              }

              if (
                ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(
                  e.key,
                )
              ) {
                e.preventDefault();
                e.stopPropagation();
                navigateWithArrowKeys(
                  rowId,
                  field,
                  {
                    ArrowUp: "up",
                    ArrowDown: "down",
                    ArrowLeft: "left",
                    ArrowRight: "right",
                  }[e.key],
                );
                return;
              }

              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                navigateToNextCell(rowId, field);
              }
            }}
            autoComplete="off"
          />
        </div>
      );
    },
    [
      items,
      activeSuggestionRowId,
      showSuggestions,
      suggestions,
      selectedSuggestionIndex,
      updateItemWithFlagReset,
      updateItemWithTotalRecalculation,
      handleCellFocus,
      handleCellBlur,
      debouncedFetchSuggestions,
      navigateToNextCell,
      navigateWithArrowKeys,
      selectCodeSuggestion,
      setSelectedSuggestionIndex,
      setShowSuggestions,
      setActiveSuggestionRowId,
      onItemsChange,
      editingDescriptionCell,
      descriptionOriginalValue,
      setEditingDescriptionCell,
      setDescriptionOriginalValue,
    ],
  );

  // MODIFIED: renderSelectCell now handles lazy loading for unit field and caps lock input fixes
  const renderSelectCell = useCallback(
    (params, field, options) => {
      const item = items.find((item) => item.id === params.row.id);
      const rowId = params.row.id;
      const isUnitField = field === "unit";

      // Ensure current value is always available in options
      let validOptions = [...(options || [])];

      // Check if current value exists in options
      const isValidValue = validOptions.some(
        (opt) => opt.value === params.value,
      );

      // If current value is not in options, add it as a temporary option
      if (params.value && !isValidValue) {
        validOptions.push({ value: params.value, label: params.value });
      }

      const safeValue = params.value || "";

      if (isUnitField) {
        const autocompleteOptions = validOptions.map((opt) => opt.value);
        return (
          <div data-row-id={rowId} data-field={field}>
            <Autocomplete
              freeSolo
              size="small"
              options={autocompleteOptions}
              value={safeValue}
              openOnFocus={false}
              autoCapitalize="characters"
              onOpen={async () => {
                if (item?.code && !unitsFetchedForRows.has(rowId)) {
                  await fetchUnitOptions(item.code, rowId, params.value);
                }
              }}
              // FIXED: Using standard change interception with safety fallback parameters
              onChange={(e, newValue) => {
                updateItem(rowId, field, (newValue || "")?.toUpperCase());
              }}
              // FIXED: Added checking conditions for 'input' mutation reasons to support clean Shift/Caps updates
              onInputChange={(e, newValue, reason) => {
                if (reason === "input" || reason === "clear") {
                  updateItem(rowId, field, (newValue || "")?.toUpperCase());
                }
              }}
              onBlur={() => {
                handleCellBlur();
              }}
              renderInput={(inputParams) => (
                <TextField
                  {...inputParams}
                  variant="standard"
                  onFocus={(e) => {
                    e.stopPropagation();
                    handleCellFocus(item);
                    setFocusedCell({ rowId, field });
                  }}
                  onKeyDown={(e) => {
                    // FIXED: Prevent native execution rules from dropping programmatic tracking modifiers
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      navigateToNextCell(rowId, field);
                    }
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: "0.875rem",
                      padding: "0 4px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "2px 4px",
                    },
                    "& input": {
                      textTransform: "uppercase",
                    },
                  }}
                />
              )}
              sx={{
                "& .MuiAutocomplete-inputRoot": {
                  padding: "0 4px",
                  flexWrap: "nowrap",
                },
              }}
            />
          </div>
        );
      }

      return (
        <div data-row-id={rowId} data-field={field}>
          <FormControl size="small" fullWidth variant="standard">
            <Select
              value={safeValue}
              disabled={disabledFields.includes(field)}
              onChange={(e) => {
                e.stopPropagation();
                updateItem(params.row.id, field, e.target.value);
              }}
              onFocus={(e) => {
                e.stopPropagation();
                handleCellFocus(item);
                setFocusedCell({ rowId, field });
              }}
              onBlur={(e) => {
                e.stopPropagation();
                handleCellBlur();
              }}
              onKeyDown={(e) => {
                if (
                  ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(
                    e.key,
                  ) &&
                  !e.altKey
                ) {
                  const isDropdownOpen = document.querySelector(
                    ".MuiMenu-root, .MuiPopover-root",
                  );
                  if (!isDropdownOpen) {
                    e.preventDefault();
                    e.stopPropagation();

                    const direction = {
                      ArrowUp: "up",
                      ArrowDown: "down",
                      ArrowLeft: "left",
                      ArrowRight: "right",
                    }[e.key];

                    navigateWithArrowKeys(rowId, field, direction);
                    return;
                  }
                }

                if (e.key === "Enter") {
                  e.preventDefault();
                  navigateToNextCell(rowId, field);
                }
              }}
              disableUnderline
              sx={{
                border: "none",
                fontFamily: "inherit",
                position: "relative",
                fontSize: "0.875rem",
                opacity: disabledFields.includes(field) ? 1 : 1,
                backgroundColor: disabledFields.includes(field)
                  ? "#f3f4f6"
                  : "transparent",
                cursor: disabledFields.includes(field)
                  ? "not-allowed"
                  : "inherit",
                "& .MuiSelect-select": {
                  cursor: disabledFields.includes(field)
                    ? "not-allowed"
                    : "inherit",
                  color: disabledFields.includes(field) ? "#9ca3af" : "inherit",
                },

                "& .MuiDataGrid-cell": {
                  borderColor: "#e5e7eb",
                  padding: 0,
                  "&:focus, &:focus-within": { outline: "none" },
                  "&:focus-within": {
                    outline: "2px solid #3b82f6",
                    outlineOffset: "-1px",
                    backgroundColor: "#eff6ff",
                  },
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f9fafb",
                  borderBottom: "1px solid #d1d5db",
                },

                "& .MuiDataGrid-columnHeader": {
                  fontWeight: 600,
                  color: "#374151",
                  backgroundColor: "#e5e7eb",
                  borderRight: "1px solid #e5e7eb",
                  "&:last-of-type": { borderRight: "none" },
                },
                "& .MuiDataGrid-columnSeparator": {
                  color: "#d1d5db",
                  visibility: "visible",
                },
                "& .MuiDataGrid-row:hover": { backgroundColor: "#f3f4f6" },
                "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row:focus-within":
                  {
                    outline: "none",
                  },
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "#dbeafe",
                  "&:hover": { backgroundColor: "#bfdbfe" },
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#fafafa",
                },
              }}
            >
              {validOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      );
    },
    [
      items,
      updateItem,
      handleCellFocus,
      handleCellBlur,
      navigateToNextCell,
      navigateWithArrowKeys,
      fetchUnitOptions,
      unitsFetchedForRows,
    ],
  );

  const renderVatToggle = useCallback(
    (params, field) => {
      const rowId = params.row.id;
      const isChecked = params.value !== false;

      // Check if VAT is applied at document level
      const isVatApplied = documentData?.vatStatus === "VAT Applied";

      const effectiveVatStatus = isVatApplied ? isChecked : false;

      return (
        <div
          data-row-id={rowId}
          data-field={field}
          className="flex items-center justify-center h-full"
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Only allow toggle if VAT is applied at document level
              if (isVatApplied) {
                updateItem(params.row.id, field, !isChecked);
              }
            }}
            onFocus={(e) => {
              e.stopPropagation();
              setFocusedCell({ rowId, field });
              setFocusedRowId(rowId);
            }}
            onKeyDown={(e) => {
              if (
                ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(
                  e.key,
                )
              ) {
                e.preventDefault();
                e.stopPropagation();

                const direction = {
                  ArrowUp: "up",
                  ArrowDown: "down",
                  ArrowLeft: "left",
                  ArrowRight: "right",
                }[e.key];

                navigateWithArrowKeys(rowId, field, direction);
                return;
              }

              if (e.key === "Enter") {
                e.preventDefault();
                navigateToNextCell(rowId, field);
              }
            }}
            disabled={!isVatApplied}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              effectiveVatStatus
                ? "bg-blue-500 text-white"
                : "bg-gray-300 text-gray-700"
            } ${
              !isVatApplied ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            }`}
            title={!isVatApplied ? "VAT must be applied at document level" : ""}
          >
            {effectiveVatStatus ? `${VATPERCENTAGE}%` : "0%"}{" "}
          </button>
        </div>
      );
    },
    [updateItem, navigateToNextCell, navigateWithArrowKeys, documentData],
  );

  const renderToggleCell = useCallback(
    (params, field, activeValue = "Yes", inactiveValue = "No") => {
      const rowId = params.row.id;
      const isActive = params.value === activeValue;

      return (
        <div
          data-row-id={rowId}
          data-field={field}
          className="flex items-center justify-center h-full"
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              updateItem(
                params.row.id,
                field,
                isActive ? inactiveValue : activeValue,
              );
            }}
            onFocus={(e) => {
              e.stopPropagation();
              setFocusedCell({ rowId, field });
              setFocusedRowId(rowId);
            }}
            onKeyDown={(e) => {
              if (
                ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(
                  e.key,
                )
              ) {
                e.preventDefault();
                e.stopPropagation();
                const direction = {
                  ArrowUp: "up",
                  ArrowDown: "down",
                  ArrowLeft: "left",
                  ArrowRight: "right",
                }[e.key];
                navigateWithArrowKeys(rowId, field, direction);
                return;
              }
              if (e.key === "Enter") {
                e.preventDefault();
                navigateToNextCell(rowId, field);
              }
            }}
            className={`w-7 h-7 flex items-center justify-center rounded text-sm font-bold transition-colors cursor-pointer ${
              isActive
                ? "bg-green-500 text-white hover:bg-green-600"
                : "bg-red-500 text-white hover:bg-red-600"
            }`}
            title={isActive ? "Click to deactivate" : "Click to activate"}
          >
            {isActive ? <Plus size={12} /> : <Minus size={12} />}
          </button>
        </div>
      );
    },
    [updateItem, navigateToNextCell, navigateWithArrowKeys],
  );

  const columns = useMemo(() => {
    const isBranchTransfer = documentData?.documentType === "BRANCHTRANSFER";
    const filteredColumns = columnManagerColumns.filter((col) => {
      if (
        !isBranchTransfer &&
        ["margin", "billedPrice", "billedAmount"].includes(col.field)
      ) {
        return false;
      }
      return true;
    });

    const baseColumns = filteredColumns.map((column) => {
      const field = column.field;

      if (field === "location") {
        return getBaseColumn(
          field,
          { [field]: column.width },
          {
            renderEditableCell,
            renderSelectCell,
            renderVatToggle,
            renderToggleCell,
          },
          locationOptions,
          undefined,
          documentData,
        );
      } else if (field === "unit") {
        return getBaseColumn(
          field,
          { [field]: column.width },
          {
            renderEditableCell,
            renderSelectCell: (params, field) =>
              renderSelectCell(
                params,
                field,
                rowUnitOptions[params.row.id] || [],
              ),
            renderVatToggle,
          },
          undefined,
          undefined,
          documentData,
        );
      } else if (field === "salesperson") {
        return getBaseColumn(
          field,
          { [field]: column.width },
          {
            renderEditableCell,
            renderSelectCell: (params, field) =>
              renderSelectCell(params, field, salesmanOptions || []),
            renderVatToggle,
          },
          undefined,
          undefined,
          documentData,
        );
      } else {
        return getBaseColumn(
          field,
          { [field]: column.width },
          {
            renderEditableCell,
            renderSelectCell,
            renderVatToggle,
            renderToggleCell,
          },
          undefined,
          undefined,
          documentData,
        );
      }
    });

    return baseColumns;
  }, [
    columnManagerColumns,
    renderEditableCell,
    renderSelectCell,
    renderVatToggle,
    locationOptions,
    rowUnitOptions,
    getBaseColumn,
    salesmanOptions,
    documentData,
    disabledFields,
  ]);

  useEffect(() => {
    return () => {
      clearHoverTimeout();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      currentRequestRef.current = null;
    };
  }, [clearHoverTimeout]);

  const totalOtherCost = useMemo(() => {
    if (!otherCosts || otherCosts.length === 0) return "0";
    const total = otherCosts
      .filter(
        (oc) =>
          oc.drAcc &&
          oc.crAcc &&
          oc.drAcc.toString().trim() !== "" &&
          oc.crAcc.toString().trim() !== "",
      )
      .reduce((s, oc) => s + (Number(oc.total) || Number(oc.amount) || 0), 0);

    return Number.isInteger(total) ? String(total) : total.toFixed(2);
  }, [otherCosts]);

  return (
    <div
      ref={tableRef}
      className="lg:w-[calc(100vw-6vw)] w-dvw overflow-x-auto lg:px-10"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "F2" && focusedRowId !== null) {
          e.preventDefault();
          openProductModal(focusedRowId);
        }
      }}
    >
      <div className="bg-gray-50 shadow w-full h-full overflow-x-auto rounded-lg border ">
        <div className="max-h-[500px] overflow-y-auto overflow-x-auto ">
          <DataGrid
            rows={calculatedItems}
            columns={columns}
            checkboxSelection
            onRowSelectionModelChange={(newSelection) => {
              setSelectedRowIds(newSelection);
            }}
            rowSelectionModel={selectedRowIds}
            onColumnResize={handleColumnResize}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleVisibilityChange}
            hideFooter
            disableRowSelectionOnClick
            columnHeaderHeight={45}
            disableSelectionOnClick
            autoHeight
            rowHeight={40}
            headerHeight={38}
            disableColumnMenu
            disableVirtualization={false}
            disableExtendRowFullWidth={true}
            getRowClassName={(params) => {
              if (params.row._belowCost) return "row-below-cost";
              return "";
            }}
            componentsProps={{
              row: {
                onKeyDown: (e) => {
                  if (
                    [
                      "ArrowUp",
                      "ArrowDown",
                      "ArrowLeft",
                      "ArrowRight",
                    ].includes(e.key)
                  ) {
                    e.stopPropagation();
                  }
                },
              },
            }}
            onRowClick={(params) => {
              setFocusedRowId(params.row.id);
            }}
            onCellClick={(params) => {
              setFocusedRowId(params.row.id);
            }}
            sx={{
              border: "none",
              fontFamily: "inherit",
              position: "relative",
              fontSize: "0.875rem",
              "& .MuiDataGrid-cell": {
                borderColor: "#e5e7eb",
                padding: 0,
                "&:focus, &:focus-within": {
                  outline: "none",
                  backgroundColor: "inherit",
                },
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f9fafb",
                borderBottom: "1px solid #d1d5db",
              },
              "& .MuiDataGrid-columnHeader": {
                fontWeight: 600,
                color: "#374151",
                backgroundColor: "#e5e7eb",
                borderRight: "1px solid #e5e7eb",
                "&:last-of-type": {
                  borderRight: "none",
                },
              },
              "& .MuiDataGrid-columnSeparator": {
                color: "#d1d5db",
                visibility: "visible",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f3f4f6",
              },
              "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row:focus-within":
                {
                  outline: "2px solid #3b82f6",
                  outlineOffset: "-1px",
                },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "inherit",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#fafafa",
              },
              "& .MuiDataGrid-row.row-below-cost": {
                backgroundColor: "#fee2e2",
                "&:hover": {
                  backgroundColor: "#fecaca",
                },
              },
            }}
          />
        </div>
        {showSuggestions && activeSuggestionRowId && hideAddButton && (
          <InvoiceSuggestionList
            suggestions={suggestions}
            selectCodeSuggestion={selectCodeSuggestion}
            selectedSuggestionIndex={selectedSuggestionIndex}
            setSelectedSuggestionIndex={setSelectedSuggestionIndex}
            activeSuggestionRowId={activeSuggestionRowId}
            showSuggestions={showSuggestions}
            loading={loading}
            onLoadMore={loadMoreSuggestions}
            hasMoreSuggestions={hasMoreSuggestions}
            isFetchingMore={isFetchingMore}
            currentQuery={currentSearchQuery}
          />
        )}

        <div className="p-2 border-t bg-gray-50 text-right rounded-b flex items-center justify-between ">
          <div className="flex items-center justify-start gap-2">
            {selectedRowIds?.length > 0 && (
              <button
                onClick={deleteSelectedItems}
                disabled={selectedRowIds?.length === 0}
                className={`inline-flex items-center px-2 py-2 text-white rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                  selectedRowIds?.length > 0
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-red-300"
                }`}
              >
                <Trash className="w-4 h-4 mr-2 lg:flex hidden" />
                Delete
              </button>
            )}

            {selectedRowIds?.length === 1 && (
              <button
                onClick={insertRowAbove}
                className="inline-flex items-center px-2 py-2 bg-indigo-500 text-white rounded-lg text-sm hover:bg-indigo-600"
              >
                <VscInsert className="w-4 h-4 mr-2 lg:flex hidden" />
                Insert Row
              </button>
            )}
            {showOtherCostButton && (
              <button
                onClick={onShowOtherCost}
                className="inline-flex items-center px-2 py-2 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600"
              >
                {isOtherCostVisible ? (
                  <>
                    <EyeOff className="w-4 h-4 mr-2 lg:flex hidden" />
                    Hide Other Cost{" "}
                    <span className="ml-2">({totalOtherCost})</span>
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-2 lg:flex hidden" />
                    Show Other Cost{" "}
                    <span className="ml-2">({totalOtherCost})</span>
                  </>
                )}
              </button>
            )}
          </div>

          <div className="flex items-center lg:gap-2 gap-1">
            <button
              onClick={openProductModal}
              className="lg:hidden inline-flex items-center px-2 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"
            >
              Stocks
            </button>
            <button
              onClick={() => setShowColumnManager(true)}
              className="inline-flex items-center px-2 py-2 bg-gray-600 text-white rounded-lg text-sm hover:bg-gray-700"
            >
              <Settings className="w-4 h-4 mr-2 lg:flex hidden" />
              Columns
            </button>
            {hideAddButton && (
              <button
                onClick={addItem}
                className="inline-flex items-center px-2 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2 lg:flex hidden" />
                Add Row{" "}
                <span className="lg:flex hidden text-xs text-gray-300 ml-1 font-normal">
                  (Alt+A)
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      <ColumnManagingModal
        showColumnManager={showColumnManager}
        setShowColumnManager={setShowColumnManager}
        columnVisibilityModel={columnVisibilityModel}
        columnsOrder={columnsOrder}
        handleDragEnd={handleDragEnd}
        handleDragStart={handleDragStart}
        handleDragOver={handleDragOver}
        handleDragLeave={handleDragLeave}
        handleDrop={handleDrop}
        isDragging={isDragging}
        dragOverIndex={dragOverIndex}
        draggedColumn={draggedColumn}
        toggleColumnVisibility={toggleColumnVisibility}
        resetColumnOrder={resetColumnOrder}
        getColumnDisplayName={getColumnDisplayName}
        moveColumn={moveColumn}
      />

      {hideAddButton && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={"Products List"}
          size="xxl"
          header={true}
        >
          <div className="product-modal-container">
            <ProductListing
              modalSuggestions={modalSuggestions}
              handleProductSelect={(product) => {
                // console.log("Selected product in modal:", product);
                if (focusedRowId) {
                  selectCodeSuggestion(focusedRowId, product);
                  setIsModalOpen(false);
                }
              }}
              onSearchQueryChange={handleModalSearchQueryChange}
              loading={modalLoading}
              setSearchBy={setSearchBy}
              searchBy={searchBy}
              pageNumber={modalPageNumber}
              pageSize={modalPageSize}
              totalRows={modalTotalRows}
              onPageChange={setModalPageNumber}
              searchQuery={modalSearchQuery}
              onRefresh={() => {
                const currentRow = items.find((r) => r.id === focusedRowId);
                const rowLocation = currentRow?.location || departmentLocation;
                fetchProductDataForModal(
                  modalSearchQuery,
                  searchBy,
                  rowLocation,
                  modalPageNumber,
                  modalPageSize,
                );
              }}
            />
          </div>
        </Modal>
      )}

      {/* ReuseSidebar for detailed analytics / transactions */}
      {sidebarOpen && (
        <ReuseSidebar
          isOpen={sidebarOpen}
          onClose={() => {
            setSidebarOpen(false);
            setActiveComponent(null);
            setSelectedProductForSidebar(null);
          }}
          title={
            activeComponent?.fullLabel || activeComponent?.label || "Details"
          }
          button={activeComponent?.button || ""}
          selectedProduct={selectedProductForSidebar}
          width={1000}
        >
          {renderSidebarContent()}
        </ReuseSidebar>
      )}
    </div>
  );
};

export default GenericInvoiceTable;
